import { Card, CardActionArea, Stack, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { OnboardingGoal } from './onboarding-goals';

export type GoalOption = { value: OnboardingGoal; icon: SvgIconComponent };

interface GoalStepLayoutProps {
    value: OnboardingGoal | '';
    onSelect: (goal: OnboardingGoal) => void;
    /** Root of the copy for this step. Title, description and every option read from under it. */
    keyPrefix: string;
    headerIcon: SvgIconComponent;
    options: GoalOption[];
}

/**
 * The shared shape of the goal step: a header and a column of selectable cards.
 *
 * Holds no opinion about what happens next. The Andie and guided steps supply their own copy and
 * icons, so the two can drift apart in wording without either one duplicating this markup.
 */
const GoalStepLayout = ({
    value,
    onSelect,
    keyPrefix,
    headerIcon: HeaderIcon,
    options,
}: GoalStepLayoutProps) => {
    const { t } = useTranslation();

    return (
        <Stack>
            <Stack alignItems="center" py={3} px={2}>
                <Stack alignItems="center" textAlign="center">
                    <Stack
                        sx={{
                            aspectRatio: 1,
                            p: 2,
                            borderRadius: '50%',
                            color: 'primary.contrastText',
                            bgcolor: 'primary.light',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 1,
                        }}
                    >
                        <HeaderIcon fontSize="large" />
                    </Stack>
                    <Typography variant="subtitle1" fontSize="large" gutterBottom>
                        {t(`${keyPrefix}.title`)}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        {t(`${keyPrefix}.description`)}
                    </Typography>
                </Stack>
            </Stack>
            <Stack gap={1.5} px={1}>
                {options.map(({ value: goalValue, icon: Icon }) => {
                    const selected = value === goalValue;
                    return (
                        <Card
                            key={goalValue}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                borderColor: selected ? 'primary.main' : 'divider',
                                borderWidth: selected ? 2 : 1,
                                bgcolor: selected ? 'primary.light' : 'background.paper',
                            }}
                        >
                            <CardActionArea
                                onClick={() => onSelect(goalValue)}
                                sx={{ p: 2 }}
                                data-id={`onboarding-goal-${goalValue}`}
                            >
                                <Stack direction="row" alignItems="center" gap={2}>
                                    <Stack
                                        sx={{
                                            color: selected ? 'primary.contrastText' : 'primary.main',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Icon fontSize="large" />
                                    </Stack>
                                    <Stack>
                                        <Typography
                                            variant="subtitle2"
                                            color={selected ? 'primary.contrastText' : 'textPrimary'}
                                        >
                                            {t(`${keyPrefix}.option.${goalValue}.label`)}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color={selected ? 'primary.contrastText' : 'textSecondary'}
                                        >
                                            {t(`${keyPrefix}.option.${goalValue}.description`)}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Stack>
        </Stack>
    );
};

export default GoalStepLayout;
